import { getConditionLabel } from "@features/ax/utils";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Activity } from "lucide-react";
import React from "react";

interface FacilityKeyStatsCardProps {
  data: {
    ageInYears: number;
    conditionScore?: number;
    conditionStatus: string;
    riskStatus: string;
    capacityUtilization: string;
    efficiencyScore: any;
    riskScore: any;
  };
}

const getScoreColor = (score: number) => {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-orange-600";
  return "text-red-600";
};

const getRiskColor = (score: number) => {
  if (score >= 70) return "text-red-600";
  if (score >= 40) return "text-yellow-600";
  return "text-green-600";
};

export const FacilityKeyStatsCard: React.FC<FacilityKeyStatsCardProps> = ({
  data,
}) => {
  const { label, color } = getConditionLabel(data?.conditionScore);

  return (
    <Card className="border-green-200">
      <CardHeader className="pb-1">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-green-600" />
          <CardTitle className="text-lg font-semibold">Key Stats</CardTitle>
        </div>
        <p className="text-sm text-gray-600">
          Performance indicators and health metrics
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Age</span>
            <CustomTooltip message="The total time the facility has been in service since its commission date." />
          </div>
          <span className="font-semibold">{data.ageInYears} years</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Condition Score</span>
            <CustomTooltip message="The overall physical health of the facility, calculated from the condition of its individual systems and assets." />
          </div>
          <div className="text-right">
            {data.conditionScore && (
              <span className={`font-semibold ${color}`}>
                {data.conditionScore}
              </span>
            )}
            <Badge
              variant="secondary"
              className={`text-xs px-2 py-1 ml-2 ${color}`}
            >
              {data.conditionStatus}
            </Badge>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Risk Score</span>
            <CustomTooltip message="The overall risk associated with this facility, considering its criticality, redundancy, and potential impact of failure." />
          </div>
          <div className="text-right">
            {/* {data.riskScore && ( */}
            <span
              className={`font-semibold ${getRiskColor(data?.riskScore || 0)}`}
            >
              {data?.riskScore ?? 0}/100
            </span>
            {/* )} */}
            <Badge variant="secondary" className={`text-xs px-2 py-1 ml-2`}>
              {data.riskStatus}
            </Badge>
          </div>
        </div>

        {/* <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Efficiency</span>
            <CustomTooltip message="A measure of the facility's performance, comparing its actual output to its energy or resource consumption against design standards" />
          </div>
          <span className="font-semibold text-green-600">
            {data.efficiencyScore}%
          </span>
        </div> */}
      </CardContent>
    </Card>
  );
};
